import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useSEO } from '../../../hooks/shared';
import { stateName } from '../../../lib/states';
import { authoritiesAPI } from '../../authorities/api/authorities';
import { voteAPI } from '../api/vote';

const TYPE_STYLES = {
  primary: 'bg-purple-100 text-purple-700',
  general: 'bg-primary-100 text-primary-700',
  special: 'bg-amber-100 text-amber-700',
  runoff: 'bg-orange-100 text-orange-700',
};

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  broken: 'bg-red-100 text-red-700',
  not_found: 'bg-orange-100 text-orange-700',
  unknown: 'bg-neutral-100 text-neutral-500',
};

function daysUntil(date) {
  const diff = new Date(date).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function SuggestForm({ authorityId, onSuccess }) {
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL (include https://)');
      return;
    }
    setSaving(true);
    try {
      await authoritiesAPI.submitUrl(authorityId, url, notes || undefined);
      onSuccess();
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">
          Official election website URL
        </label>
        <input
          type="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError('');
          }}
          placeholder="https://vote.countyname.gov/"
          className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">Notes (optional)</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. County clerk's election page"
          className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button
        type="submit"
        disabled={saving || !url}
        className="w-full py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
      >
        {saving ? 'Submitting...' : 'Submit suggestion'}
      </button>
    </form>
  );
}

export default function VoteLocation() {
  const { state, location } = useParams();
  const navigate = useNavigate();
  const stateUpper = state.toUpperCase();
  const fullState = stateName(stateUpper);
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vote-location', state, location],
    queryFn: () => voteAPI.getLocation(state, location),
  });

  const authority = data?.authority;
  const displayName = authority?.county
    ? `${authority.county} County`
    : authority?.name || location.replace(/-/g, ' ');

  useSEO({
    title: authority ? `Voting in ${displayName}, ${fullState}` : `Voting in ${fullState}`,
    description: authority
      ? `Voting information for ${displayName}, ${fullState}. Find election dates, official election website, registration deadlines, and polling locations.`
      : `Find voting information for ${fullState}.`,
  });

  if (isLoading) {
    return (
      <div className="bg-neutral-50 min-h-screen py-8">
        <div className="container-custom max-w-3xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-neutral-200 rounded w-2/3" />
            <div className="h-4 bg-neutral-200 rounded w-1/3" />
            <div className="h-48 bg-white rounded-2xl border border-neutral-200" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !authority) {
    return (
      <div className="bg-neutral-50 min-h-screen py-8">
        <div className="container-custom max-w-3xl text-center py-16">
          <p className="text-neutral-500 mb-4">Location not found.</p>
          <Link
            to={`/vote/${state}`}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Back to {fullState}
          </Link>
        </div>
      </div>
    );
  }

  const { stateAuthority, elections, jurisdictionSource } = data;
  const nextElection = elections?.[0];
  const checks = authority.websiteChecks || [];

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      <div className="container-custom max-w-3xl">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm text-neutral-500 mb-6 flex-wrap">
          <Link to="/vote" className="hover:text-neutral-700">
            Vote
          </Link>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <Link to={`/vote/${state}`} className="hover:text-neutral-700">
            {fullState}
          </Link>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-neutral-900 font-medium">{displayName}</span>
        </div>

        {/* Header card */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm mb-4">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-serif font-bold text-neutral-900">
                Voting in {displayName}, {stateUpper}
              </h1>
              <p className="text-neutral-500 text-sm mt-1">
                {authority.jurisdictionType} · {fullState}
              </p>
            </div>
            <span
              className={`shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${STATUS_STYLES[authority.websiteStatus] || STATUS_STYLES.unknown}`}
            >
              {authority.websiteStatus}
            </span>
          </div>

          {/* Official website */}
          {authority.websiteUrl ? (
            <a
              href={authority.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              Official Election Website
            </a>
          ) : (
            <p className="text-neutral-400 italic text-sm">No official website on file</p>
          )}

          {/* Fallback: state-level website */}
          {!authority.websiteUrl && stateAuthority?.websiteUrl && (
            <div className="mt-3">
              <p className="text-xs text-neutral-500 mb-1">State-level resource:</p>
              <a
                href={stateAuthority.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {stateAuthority.name} — {new URL(stateAuthority.websiteUrl).hostname}
              </a>
            </div>
          )}

          {authority.lastCheckedAt && (
            <p className="text-xs text-neutral-400 mt-3">
              Last verified{' '}
              {new Date(authority.lastCheckedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
        </div>

        {/* Registration deadline highlight */}
        {nextElection?.registrationDeadline && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="font-semibold text-amber-900">Registration Deadline</h3>
            </div>
            <p className="text-amber-800">
              Register by{' '}
              <span className="font-bold">
                {new Date(nextElection.registrationDeadline).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>{' '}
              for the {nextElection.name}.
            </p>
          </div>
        )}

        {/* Upcoming elections */}
        {elections.length > 0 && (
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-neutral-900 mb-3">Upcoming Elections</h2>
            <div className="space-y-2">
              {elections.map((election) => {
                const days = daysUntil(election.electionDate);
                const type = (election.electionType || 'general').toLowerCase();
                return (
                  <div
                    key={election.id}
                    className="bg-white rounded-xl border border-neutral-200 p-4 hover:border-primary-200 transition-colors cursor-pointer"
                    onClick={() => navigate(`/elections/${election.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`px-2 py-0.5 text-xs font-semibold rounded-full ${TYPE_STYLES[type] || 'bg-neutral-100 text-neutral-600'}`}
                          >
                            {election.electionType || 'General'}
                          </span>
                          {days > 0 && (
                            <span
                              className={`text-xs ${days <= 30 ? 'text-red-600' : 'text-emerald-600'}`}
                            >
                              {days} days away
                            </span>
                          )}
                        </div>
                        <p className="font-medium text-neutral-900 text-sm">{election.name}</p>
                        <p className="text-xs text-neutral-500">
                          {new Date(election.electionDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                      <svg
                        className="w-4 h-4 text-neutral-400 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>

                    {(election.earlyVotingStart || election.absenteeBallotDeadline) && (
                      <div className="mt-2 pt-2 border-t border-neutral-100 flex flex-wrap gap-x-5 gap-y-1 text-xs text-neutral-500">
                        {election.earlyVotingStart && (
                          <span>
                            <span className="font-medium text-neutral-600">Early voting:</span>{' '}
                            {new Date(election.earlyVotingStart).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                            {election.earlyVotingEnd &&
                              ` – ${new Date(election.earlyVotingEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                          </span>
                        )}
                        {election.absenteeBallotDeadline && (
                          <span>
                            <span className="font-medium text-neutral-600">Absentee:</span>{' '}
                            {new Date(election.absenteeBallotDeadline).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Official resources */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm mb-4">
          <h2 className="font-semibold text-neutral-900 mb-3">Official Resources</h2>
          <div className="space-y-2">
            {authority.websiteUrl && (
              <a
                href={authority.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
              >
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                {displayName} Election Website
              </a>
            )}
            {stateAuthority?.websiteUrl && (
              <a
                href={stateAuthority.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
              >
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                {fullState} Secretary of State
              </a>
            )}
            {jurisdictionSource?.url && (
              <a
                href={jurisdictionSource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
              >
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Official Election Info Source
              </a>
            )}
          </div>
        </div>

        {/* Suggest URL */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm mb-4">
          {submitted ? (
            <div className="flex items-center gap-2 text-emerald-700">
              <svg
                className="w-5 h-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-sm font-medium">
                Thanks! Your suggestion has been submitted for review.
              </span>
            </div>
          ) : showSuggestForm ? (
            <>
              <h2 className="font-semibold text-neutral-900 mb-3">
                {authority.websiteUrl ? 'Suggest a correction' : 'Add the official website'}
              </h2>
              <SuggestForm
                authorityId={authority.id}
                onSuccess={() => {
                  setShowSuggestForm(false);
                  setSubmitted(true);
                }}
              />
              <button
                onClick={() => setShowSuggestForm(false)}
                className="mt-3 w-full text-xs text-neutral-400 hover:text-neutral-500"
              >
                Cancel
              </button>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-900">
                  {authority.websiteUrl ? 'Know a better URL?' : 'Know the official website?'}
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Help other voters by suggesting the official link.
                </p>
              </div>
              <button
                onClick={() => setShowSuggestForm(true)}
                className="shrink-0 ml-4 px-3 py-1.5 text-xs font-medium border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
              >
                {authority.websiteUrl ? 'Suggest correction' : 'Add website'}
              </button>
            </div>
          )}
        </div>

        {/* Recent checks */}
        {checks.length > 0 && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm">
            <h2 className="font-semibold text-neutral-900 mb-3 text-sm">Recent website checks</h2>
            <div className="space-y-2">
              {checks.map((check) => (
                <div
                  key={check.id}
                  className="flex items-center justify-between text-xs text-neutral-500"
                >
                  <span>{new Date(check.checkedAt).toLocaleString()}</span>
                  <div className="flex items-center gap-3">
                    {check.httpStatus && (
                      <span
                        className={check.httpStatus < 400 ? 'text-emerald-600' : 'text-red-500'}
                      >
                        HTTP {check.httpStatus}
                      </span>
                    )}
                    {check.changeDetected && (
                      <span className="text-amber-600 font-medium">Changed</span>
                    )}
                    {check.responseTimeMs && <span>{check.responseTimeMs}ms</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
